import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Container, Loading, ErrorMessage, SimpleButton } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { StudentExamViewLoadDTO, ExamDetailsDTO } from './models/StudentExamViewDTOs';
import AssessmentNavigationMatrix from './components/AssessmentNavigationMatrix';

const AttemptSummaryPage: React.FC = () => {
    document.title = 'Exam View';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [navData, setNavData] = useState<ExamDetailsDTO | null>(null);

    const { attemptId = '0', lastQuestionId = '0' } = useParams();

    const navigate = useNavigate();

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<StudentExamViewLoadDTO>(
                    'get',
                    `assessments/studentExamView/studentExamAttemptQuestion?attemptId=${attemptId}&attemptQuestionId=${lastQuestionId}`
                );
                const data = res.data;
                setNavData(data.examAttemptDetails);
            } catch {
                setError('Failed to load navigation data.');
            } finally {
                setLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async () => {
        try {
            setError('');
            setLoading(true);
            await apiRequest('post', `assessments/studentExamView/submitExam/${attemptId}`);
            navigate(`/studentExamView/attempt/${attemptId}/results`, { replace: true });
        } catch {
            setError('Failed to submit exam.');
        } finally {
            setLoading(false);
        }
    };

    const areAllQuestionsAnswered = (() => {
        if (navData === null) {
            return false;
        }

        return navData.navigationQuestions.every((question) => question.isAnswered);
    })();

    return (
        <Container className="user-min-font-size">
            <div className="mt-12">
                Please review your exam. You can go back and edit any questions in the most recent
                segment.
            </div>
            <div>Click Submit when you are ready to submit your exam.</div>
            <br />
            <Loading visible={loading} size="lg" />
            <ErrorMessage error={error} />
            {navData && (
                <AssessmentNavigationMatrix
                    questions={navData.navigationQuestions}
                    loading={loading}
                    language={'en'}
                    activeSegmentNumber={navData.activeSegmentNumber}
                    onButtonClick={(questionRoute: string) => {
                        navigate(`/studentExamView/attempt/${attemptId}/${questionRoute}`);
                    }}
                />
            )}

            <br />
            {!areAllQuestionsAnswered && !loading && (
                <ErrorMessage error="You must answer all questions before submitting." />
            )}

            <div className="flex flex-row gap-4">
                <div>
                    <SimpleButton
                        sx={btnSxBlue}
                        onClick={() =>
                            navigate(`/studentExamView/attempt/${attemptId}/q/${lastQuestionId}`)
                        }
                        disabled={loading}
                    >
                        Back
                    </SimpleButton>
                </div>
                <div>
                    <SimpleButton
                        sx={btnSxBlue}
                        onClick={handleSubmit}
                        disabled={loading || !areAllQuestionsAnswered}
                    >
                        Submit
                    </SimpleButton>
                </div>
            </div>
        </Container>
    );
};

const btnSxBlue = {
    '&&': {
        // bumps specificity
        backgroundColor: '#20a2dc',
        color: '#fff',
        '&:hover': { backgroundColor: '#178ec2' },
        '&:disabled': {
            // keep your disabled look here or leave for your global override
            backgroundColor: '#fafafa',
            color: '#aaa',
        },
    },
};

export default AttemptSummaryPage;
